import React, { useState, useEffect } from 'react';
import TextElementControlPanel from './TextElementControlPanel';
import ImageElementControlPanel from './ImageElementControlPanel';
import ShapeElementControlPanel from './ShapeElementControlPanel';
import WorldObjectsPanel from './WorldObjectsPanel';
import { useCredits } from '../../../contexts/CreditsContext';
import { useBook } from '../../../contexts/BookContext';
import { generateCharacterImageByJSON } from '../../../services/bookService/operations/generateCharacterImageByJSON';
import { generatePlaceImageByJSON } from '../../../services/bookService/operations/generatePlaceImageByJSON';
import { generateThingImageByJSON } from '../../../services/bookService/operations/generateThingImageByJSON';
import { notify } from '../../../utils/notify.jsx';

export default function SelectedElementControlPanel({
  selectedElement,
  elements,
  onUpdateElements,
  onOpenImageEditor,
  onOpenBorderEditor,
  onSaveSpread,
  onGeneratingChange,
  currentBook,
  currentSpread,
}) {
  const [positionX, setPositionX] = useState('');
  const [positionY, setPositionY] = useState('');
  const [initialPosition, setInitialPosition] = useState(null);
  const [generatingElementId, setGeneratingElementId] = useState(null);
  const [showWorldObjects, setShowWorldObjects] = useState(false);
  const [generatingCharIdx, setGeneratingCharIdx] = useState(null);
  const [generatingPlaceUid, setGeneratingPlaceUid] = useState(null);
  const [generatingThingUid, setGeneratingThingUid] = useState(null);
  const { checkCredits, updateCredits } = useCredits();
  const { updateBook } = useBook();

  // Notify parent when generating state changes
  useEffect(() => {
    if (onGeneratingChange) {
      onGeneratingChange(generatingElementId);
    }
  }, [generatingElementId, onGeneratingChange]);

  // Update position state when selected element changes
  useEffect(() => {
    if (!selectedElement) {
      setPositionX('');
      setPositionY('');
      return;
    }

    const currentElement = elements.find((el) => el.id === selectedElement);
    if (!currentElement) return;

    const x = currentElement.position?.x || 0;
    const y = currentElement.position?.y || 0;
    setPositionX(x.toString());
    setPositionY(y.toString());
    
    // Get original position from the saved book data
    let originalPos = { x, y };
    if (currentBook && currentSpread !== undefined) {
      const spreadKey = `spread-${currentSpread}`;
      const savedElement = currentBook.spreads?.[spreadKey]?.elements?.find(
        (el) => el.id === selectedElement
      );
      if (savedElement?.position) {
        originalPos = savedElement.position;
      }
    }
    setInitialPosition(originalPos);
  }, [selectedElement, elements, currentBook, currentSpread]);

  if (!selectedElement) return null;

  const currentElement = elements.find((el) => el.id === selectedElement);
  if (!currentElement) return null;

  const isTextElement = currentElement.type === 'text';

  const handlePositionChange = (axis, value) => {
    const numValue = parseInt(value) || 0;
    const updatedElements = elements.map((el) => {
      if (el.id === selectedElement) {
        return {
          ...el,
          position: {
            ...el.position,
            [axis]: numValue,
          },
        };
      }
      return el;
    });
    onUpdateElements(updatedElements);
    
    if (axis === 'x') {
      setPositionX(value);
    } else {
      setPositionY(value);
    }
  };

  const handleResetPosition = () => {
    if (initialPosition && selectedElement) {
      const updatedElements = elements.map((el) => {
        if (el.id === selectedElement) {
          return {
            ...el,
            position: {
              ...el.position,
              x: initialPosition.x,
              y: initialPosition.y,
            },
          };
        }
        return el;
      });
      onUpdateElements(updatedElements);
      setPositionX(initialPosition.x.toString());
      setPositionY(initialPosition.y.toString());
    }
  };

  const GENERATION_COST = 5;

  const generateCharacterImage = async (uid) => {
    if (!currentBook?.id) return;
    const character = (currentBook?.world?.characters || []).find(c => (c._uid || c.id) === uid);
    if (!character) return;

    try {
      const has = await checkCredits(GENERATION_COST);
      if (!has) {
        notify.error('Insufficient credits. 5 credits required.', { toastId: 'world-gen-credits' });
        return;
      }

      setGeneratingCharIdx(uid);
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...characterNoImage } = character;
      const payload = { type: 'character', character: characterNoImage };
      const artStyle = currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      const resp = await generateCharacterImageByJSON(artStyle, JSON.stringify(payload));
      const d = JSON.parse(JSON.stringify(resp || {}));
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;

      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }

      const updated = (currentBook.world.characters || []).map((c) =>
        (c._uid || c.id) === uid ? { ...c, referenceImage: url } : c
      );
      const newWorld = { ...(currentBook.world || {}), characters: updated };
      await updateBook(currentBook.id, { world: newWorld });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
      notify.success(`Image generated for ${character.name}`, { toastId: 'world-char-gen-success' });
    } catch (err) {
      console.error('[SelectedElementControlPanel] generateCharacterImage error', err);
      notify.error(`Failed to generate image for character`, { toastId: 'world-char-gen-error' });
    } finally {
      setGeneratingCharIdx(null);
    }
  };

  const generatePlaceImage = async (uid) => {
    if (!currentBook?.id) return;
    const place = (currentBook?.world?.places || []).find(p => (p._uid || p.id) === uid);
    if (!place) return;

    try {
      const has = await checkCredits(GENERATION_COST);
      if (!has) {
        notify.error('Insufficient credits. 5 credits required.', { toastId: 'world-gen-credits' });
        return;
      }

      setGeneratingPlaceUid(uid);
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...placeNoImage } = place;
      const payload = { type: 'place', place: placeNoImage };
      const artStyle = currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      const resp = await generatePlaceImageByJSON(artStyle, JSON.stringify(payload));
      const d = JSON.parse(JSON.stringify(resp || {}));
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;

      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }

      const updated = (currentBook.world.places || []).map((p) =>
        (p._uid || p.id) === uid ? { ...p, referenceImage: url } : p
      );
      const newWorld = { ...(currentBook.world || {}), places: updated };
      await updateBook(currentBook.id, { world: newWorld });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
      notify.success(`Image generated for ${place.name}`, { toastId: 'world-place-gen-success' });
    } catch (err) {
      console.error('[SelectedElementControlPanel] generatePlaceImage error', err);
      notify.error(`Failed to generate image for place`, { toastId: 'world-place-gen-error' });
    } finally {
      setGeneratingPlaceUid(null);
    }
  };

  const generateThingImage = async (uid) => {
    if (!currentBook?.id) return;
    const thing = (currentBook?.world?.things || []).find(t => (t._uid || t.id) === uid);
    if (!thing) return;

    try {
      const has = await checkCredits(GENERATION_COST);
      if (!has) {
        notify.error('Insufficient credits. 5 credits required.', { toastId: 'world-gen-credits' });
        return;
      }

      setGeneratingThingUid(uid);
      const { imageUrl: _omitImageUrl, referenceImage: _omitReferenceImage, ...thingNoImage } = thing;
      const payload = { type: 'thing', thing: thingNoImage };
      const artStyle = currentBook?.visualStyle?.value || currentBook?.visualStyle || 'Digital SciFi Art';
      const resp = await generateThingImageByJSON(artStyle, JSON.stringify(payload));
      const d = JSON.parse(JSON.stringify(resp || {}));
      const resultText = typeof d.result === 'undefined' ? null : d.result;
      let url = null;

      if (typeof resultText === 'string') {
        if (resultText.startsWith('http')) {
          url = resultText.trim();
        } else {
          try {
            const obj = JSON.parse(resultText);
            url = obj?.imageUrl || obj?.url || null;
          } catch (_) {}
          if (!url) {
            const idxHttp = resultText.indexOf('http');
            if (idxHttp >= 0) {
              const end = resultText.indexOf(' ', idxHttp);
              url = end >= 0 ? resultText.slice(idxHttp, end) : resultText.slice(idxHttp);
            }
          }
        }
      }
      if (!url && typeof d.imageUrl === 'string') {
        url = d.imageUrl;
      }
      if (!url) {
        throw new Error('No image URL returned');
      }

      const updated = (currentBook.world.things || []).map((t) =>
        (t._uid || t.id) === uid ? { ...t, referenceImage: url } : t
      );
      const newWorld = { ...(currentBook.world || {}), things: updated };
      await updateBook(currentBook.id, { world: newWorld });
      // Charged server-side by generate*ImageByJSON (helpers/worldObjectImage.js).
      notify.success(`Image generated for ${thing.name}`, { toastId: 'world-thing-gen-success' });
    } catch (err) {
      console.error('[SelectedElementControlPanel] generateThingImage error', err);
      notify.error(`Failed to generate image for thing`, { toastId: 'world-thing-gen-error' });
    } finally {
      setGeneratingThingUid(null);
    }
  };

  return (
    <div
      className="fixed z-50 flex gap-4 pointer-events-none"
      style={{ top: '140px', right: '16px' }}
      data-selected-element-control-panel="true"
    >
      {/* World Objects Panel - slides in from right */}
      {showWorldObjects && currentElement?.type === 'image' && (
        <div className="pointer-events-auto">
          <WorldObjectsPanel
            worldObject={currentBook?.world}
            imagePrompt={currentElement?.imagePrompts?.[currentElement?.imagePrompts?.length - 1] || currentElement?.imagePrompt || ''}
            isExpanded={true}
            currentBook={currentBook}
            onGenerateCharacterImage={generateCharacterImage}
            onGeneratePlaceImage={generatePlaceImage}
            onGenerateThingImage={generateThingImage}
            generatingCharIdx={generatingCharIdx}
            generatingPlaceUid={generatingPlaceUid}
            generatingThingUid={generatingThingUid}
          />
        </div>
      )}

      {/* Toggle Button - on the left side of main panel, centered vertically */}
      {currentElement?.type === 'image' && (
        <div className="flex items-center pointer-events-auto">
          <button
            onClick={() => setShowWorldObjects(!showWorldObjects)}
            className="p-3 hover:bg-indigo-600 active:bg-indigo-700 rounded-lg transition-colors text-white bg-indigo-500 border-0 shadow-lg font-semibold"
            title={showWorldObjects ? 'Hide world objects' : 'Show world objects'}
          >
            <svg className={`w-6 h-6 transition-transform ${showWorldObjects ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      )}

      {/* Main Control Panel */}
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-[344px] max-h-[80vh] overflow-y-auto pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
      {/* Section 1: Text Element Controls */}
      {isTextElement && (
        <TextElementControlPanel
          content={currentElement.content}
          style={currentElement.style}
          onContentChange={(newContent) => {
            const updatedElements = elements.map((el) => {
              if (el.id === selectedElement) {
                return { ...el, content: newContent };
              }
              return el;
            });
            onUpdateElements(updatedElements);
          }}
          onStyleChange={(newStyle) => {
            const updatedElements = elements.map((el) => {
              if (el.id === selectedElement) {
                return {
                  ...el,
                  style: { ...el.style, ...newStyle },
                };
              }
              return el;
            });
            onUpdateElements(updatedElements);
          }}
        />
      )}

      {/* Section 1b: Image Element Controls */}
      {!isTextElement && currentElement.type === 'image' && (
         <ImageElementControlPanel
           element={currentElement}
           onOpenImageEditor={onOpenImageEditor}
           onOpenBorderEditor={onOpenBorderEditor}
           onSaveSpread={onSaveSpread}
           onGeneratingChange={setGeneratingElementId}
           onUpdate={(updates) => {
             // Handle showWorldObjects flag from modal
             if (updates.showWorldObjects) {
               setShowWorldObjects(true);
               return;
             }
             const updatedElements = elements.map((el) => {
               if (el.id === selectedElement) {
                 return { ...el, ...updates };
               }
               return el;
             });
             onUpdateElements(updatedElements);
           }}
           showWorldObjects={showWorldObjects}
         />
      )}

      {/* Section 1c: Shape Element Controls */}
      {!isTextElement && currentElement.type === 'shape' && (
        <ShapeElementControlPanel
          element={currentElement}
          onUpdate={(updates) => {
            const updatedElements = elements.map((el) => {
              if (el.id === selectedElement) {
                return { ...el, ...updates };
              }
              return el;
            });
            onUpdateElements(updatedElements);
          }}
        />
      )}

      {/* Section 2: Coordinates */}
      <div className="my-4 border-t border-gray-200" />
      
      <div className="pt-4">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-4">Coordinates</h3>
        
        {/* X Position */}
        <div className="mb-4">
          <label className="text-xs font-medium text-gray-700 block mb-1.5">X Position</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={positionX}
              onChange={(e) => handlePositionChange('x', e.target.value)}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>

        {/* Y Position */}
        <div className="mb-4">
          <label className="text-xs font-medium text-gray-700 block mb-1.5">Y Position</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={positionY}
              onChange={(e) => handlePositionChange('y', e.target.value)}
              className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="0"
            />
            <span className="text-xs text-gray-500">px</span>
          </div>
        </div>

        {/* Reset Button */}
        <button
          onClick={handleResetPosition}
          className="w-full px-3 py-2 mb-3 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded transition-colors"
          title="Reset to original position"
        >
          ↻ Reset Position
        </button>

        {/* Arrow Key Hint */}
        <div className="bg-blue-50 border border-blue-200 rounded p-2">
          <p className="text-xs text-blue-900 font-medium mb-1">💡 Keyboard Shortcuts</p>
          <ul className="text-xs text-blue-800 space-y-0.5">
            <li>• <kbd className="bg-white px-1 rounded border border-blue-300 text-xs">↑↓←→</kbd> Move 5px</li>
            <li>• <kbd className="bg-white px-1 rounded border border-blue-300 text-xs">Shift</kbd> + arrows: 10px</li>
            <li>• <kbd className="bg-white px-1 rounded border border-blue-300 text-xs">Ctrl</kbd> + arrows: 1px</li>
          </ul>
        </div>
      </div>
      </div>
    </div>
  );
}
